import { useCallback, useEffect, useRef, useState } from 'react';
import { useToast } from '../toast';
import { loginApi, loginAuthGoogleApi, registerApi, sendResetPsdEmailApi } from '~/ajax/api.client';
import { useCountDown } from '~/hooks/useCountDown';

interface onCloseFnProps {
  onCloseFn: () => void;
}

export default function Awards2024LoginDialog({ onCloseFn }: onCloseFnProps) {
  const [account, setAccount] = useState('');
  const [psd, setPsd] = useState('');
  const [loginType, setLoginType] = useState<'register' | 'login' | 'reset'>('login');
  const [confirmPsd, setConfirmPsd] = useState('');
  const [isAgree1, setIsAgress1] = useState(false);
  const [isAgree2, setIsAgress2] = useState(false);
  const [isAgree3, setIsAgress3] = useState(false);
  const [isAgree4, setIsAgress4] = useState(false);
  const [gWaterDamToken, setGoogleToken] = useState('');
  const [resetEmailState, setResetEmailState] = useState<'init' | 'countdown' | 'retry'>('init');
  const toast = useToast();
  const submitState = useRef(false);
  const handleClose = () => onCloseFn();
  const [t, { start: startCountDown, reset: resetCountDown }] = useCountDown(60, {
    done: () => {
      setResetEmailState('retry');
    }
  });

  async function onSubmit() {
    if (!account) {
      toast.error('account is required');
      return;
    }
    if (!psd && loginType !== 'reset') {
      toast.error('password is required');
      return;
    }
    if (!gWaterDamToken) {
      toast.error('please click google captcha');
      return;
    }

    if (loginType === 'register') {
      if (!isAgree1 || !isAgree2 || !isAgree3) {
        toast.error('Please check the box to agree to the privacy agreement');
        return;
      }
    }

    if (submitState.current) {
      return;
    }
    submitState.current = true;
    try {
      let res: BaseResponse<{}>;
      if (loginType === 'register') {
        res = await registerApi({
          accountName: account,
          googleToken: gWaterDamToken,
          isAgreeAdult: isAgree3 ? 1 : 0,
          isAgreeGWB: isAgree1 ? 1 : 0,
          isAgreeReceived: isAgree1 ? 1 : 0,
          isAgreeEuropean: isAgree2 ? 1 : 0,
          password: psd,
          confirmPassword: confirmPsd
        });
      } else if (loginType === 'login') {
        res = await loginApi({
          accountName: account,
          googleToken: gWaterDamToken,
          isAgree: isAgree4 ? 1 : 0, password: psd,
          loginType: 0
        });
      } else {
        resetCountDown();
        startCountDown();
        setResetEmailState('countdown');
        res = await sendResetPsdEmailApi({
          accountName: account,
          token: gWaterDamToken,
        });
      }
      submitState.current = false;
      if (res.code !== 0) {
        (window as any).grecaptcha.reset();
        setGoogleToken('');
        if (loginType === 'reset') {
          setResetEmailState('retry');
        }
        toast.error(`Login Error: ${res.msg}`);
        return;
      }
      if (loginType === 'login' || loginType === 'register') {
        return location.reload();
      }
    } catch (error) {
      submitState.current = false;
      console.error(error);
      toast.error('found unknown error');
    }
  }

  function handleInputCheckBoxChange(type: 1 | 2 | 3 | 4, v: boolean) {
    switch (type) {
      case 1:
        setIsAgress1(v);
        break;
      case 2:
        setIsAgress2(v);
        break;
      case 3:
        setIsAgress3(v);
        break;
      case 4:
        setIsAgress4(v);
        break;
      default:
        break;
    }
  }

  function switchRegister(tab: 'login' | 'register' | 'reset') {
    (window as any).grecaptcha.reset();
    setPsd('');
    setIsAgress1(false);
    setIsAgress2(false);
    setIsAgress3(false);
    setIsAgress4(false);
    setLoginType(tab);
  }

  const initGrecaptcha = useCallback(() => {
    const waterDam = document.getElementById('water-dam') as HTMLElement;
    (window as any).grecaptcha.ready(() => {
      if (waterDam && waterDam.childNodes.length > 0) {
        return;
      }
      (window as any).grecaptcha.render(
        'water-dam',
        {
          sitekey: '6LdGSR0TAAAAAG_KouYkmGyA6-835XAR8YqgIILS',
          size: 'normal',
          callback: (token: string) => {
            setGoogleToken(token);
          },
          'expired-callback': (e: any) => {
            console.log('expired', e);
            setGoogleToken('');
          }
        }
      );
    });
  }, []);

  const initGoogleLogin = useCallback(() => {
    (window as any).google.accounts.id.initialize({
      client_id: '1069509694790-54rhtp2val1296i7f1610vidbvkujc5j.apps.googleusercontent.com',
      ux_mode: 'popup',
      callback: (data: { clientId: string, credential: string }) => {
        console.log(data);
        loginAuthGoogleApi(data.credential).then((res) => {
          if (res.code === 0) {
            return location.reload();
          }
          toast.error(`Login Error: ${res.msg}`);
        })
          .catch((e) => {
            console.error(e);
            toast.error('found unknown error');
          });
      },
    });
    (window as any).google.accounts.id.renderButton(document.getElementById('login__google'), {
      type: 'icon',
      shape: 'circle',
      text: 'signin_with',
      size: 'large',
    });
  }, []);

  const renderResetEmailBtnState = useCallback(() => {
    if (resetEmailState === 'init') {
      return 'Send Email';
    }

    if (resetEmailState === 'countdown') {
      return `${t} s`;
    }

    return 'Resend Email';
  }, [t, resetEmailState]);

  useEffect(() => {
    initGrecaptcha();
    initGoogleLogin();
  }, []);

  return (
    <div className='awards-dialog'>
      <div className='awards-dialog__box login'>
        <span className='awards-dialog__close' onClick={handleClose}>X</span>
        <h3 className='login__tit'>{loginType === 'login' ? 'Log in' : 'Register'}</h3>
        <div className='login__cont'>
          <div className='login__row'>
            <label htmlFor="" className='login__label'>Account</label>
            <div className='login__input-frame'>
              <input value={account} className='login__input' type="text" placeholder='Please input your accountName' onChange={(e) => {
                const v = e.target.value || '';
                setAccount(v.trim());
              }} />
            </div>
          </div>
          {
            loginType !== 'reset' && <div className='login__row'>
              <label htmlFor="" className='login__label'>Password</label>
              <div className='login__input-frame'>
                <input value={psd} className='login__input' type="password" placeholder='Please input your account password' onChange={(e) => {
                  const v = e.target.value || '';
                  setPsd(v.trim());
                }} />
              </div>
            </div>
          }
          {
            loginType === 'register' && <div className='login__row'>
              <label htmlFor="" className='login__label'>Confirm Password</label>
              <div className='login__input-frame'>
                <input value={confirmPsd} className='login__input' type="password" placeholder='Please confirm your password' onChange={(e) => {
                  const v = e.target.value || '';
                  setConfirmPsd(v.trim());
                }} />
              </div>
            </div>
          }
          {
            loginType === 'register' && <>
              <div className="user-privilege-box">
                <input
                  className="radio-input"
                  type="checkbox"
                  id="radio-input-1"
                  checked={isAgree1}
                  onChange={(v) => {
                    handleInputCheckBoxChange(1, v.target.checked);
                  }}
                />
                <label htmlFor="radio-input-1" className="text-1">
                  1. By ticking this box, you confirm that you have read and agreed to the GWB Terms of Service
                  and the GWB <a href="/awards2024/privacyPolicy" target="_blank" className="link" rel="noreferrer">Privacy Policy</a> and understand how they apply to your use of GWB.
                </label>
              </div>
              <div className="user-privilege-box">
                <input
                  className="radio-input"
                  type="checkbox"
                  id="radio-input-2"
                  checked={isAgree2}
                  onChange={(v) => {
                    handleInputCheckBoxChange(2, v.target.checked);
                  }}
                />
                <label htmlFor="radio-input-2" className="text-1">
                  2. By ticking this box, you agree to the transfer of your data outside the European Economic Area.
                  GWB is a product offered by Tencent, a Chinese company who will
                  process your data outside the European Economic Area in order to provide the service.
                  Please note that there are risks in such a transfer including your
                  data being subject to differing legal regimes which may not afford
                  it the same level of protection as that available in the country in
                  which you are located. For more information please see our <a href="/awards2024/privacyPolicy" target="_blank" className="link" rel="noreferrer">Privacy Policy</a>.
                </label>
              </div>
              <div className="user-privilege-box">
                <input
                  className="radio-input"
                  type="checkbox"
                  id="radio-input-3"
                  checked={isAgree3}
                  onChange={(v) => {
                    handleInputCheckBoxChange(3, v.target.checked);
                  }}
                />
                <label htmlFor="radio-input-3" className="text-1">
                  3. By ticking this box you confirm you are over 18 years of age, or in the case of a region where
                  the minimum age for processing personal information differs, such different age as required.
                </label>
              </div>
              <div className="user-privilege-box">
                <input
                  className="radio-input"
                  type="checkbox"
                  id="radio-input-4"
                  checked={isAgree4}
                  onChange={(v) => {
                    handleInputCheckBoxChange(4, v.target.checked);
                  }}
                />
                <label htmlFor="radio-input-4" className="text-1">
                  4. By ticking this box you consent to receiving newsletters and promotional information
                  about the GWB Awards and other related events / activities which may be of interest. [Optional]
                </label>
              </div>
            </>
          }
          <div id='water-dam'></div>
          {
            loginType === 'reset' ? <div className='login__handle'>
              <button className='login__confirm' type='button'
                disabled={submitState.current
                  || !gWaterDamToken || resetEmailState === 'countdown'} onClick={onSubmit}>{
                  renderResetEmailBtnState()
                }</button>
            </div>
              : <>
                <div className='login__handle'>
                  <button className='login__confirm' type='button'
                    disabled={submitState.current || !gWaterDamToken} onClick={onSubmit}>{loginType === 'login'
                      ? 'Sign In' : 'Register'}</button>
                </div>
                <span className='login__split'>OR</span>
                <button className='login__google' id='login__google'>google</button>
                <div className='login__ft'>
                  {
                    loginType === 'login' ? <p className='login__ft-txt'>
                      Don't have an account? <a className='link' onClick={(e) => {
                      e.preventDefault();
                      switchRegister('register');
                    }}>Register</a>
                    </p> : <p className='login__ft-txt'>
                      Have an account? <a className='link' onClick={(e) => {
                      e.preventDefault();
                      switchRegister('login');
                    }}>Sign In</a>
                    </p>
                  }
                  {
                    loginType === 'login' && <p className='login__ft-txt'><a className='link' onClick={(e) => {
                      e.preventDefault();
                      switchRegister('reset');
                    }}>Forgot password</a></p>
                  }
                </div>
              </>
          }
        </div>
      </div>
    </div>
  );
}
